/**
 * TypeScriptExecutor — adapts a {@link Behaviour} (the TS authoring base) to the unified
 * {@link IScriptExecutor} contract. This is the first-class scripting backend.
 *
 * The compiled TS module supplies a `Behaviour` subclass (swc strips types → JS, runs via
 * the existing `new Function()` path in the player/editor); this executor binds the engine
 * context, applies inspector variable overrides, dispatches the lifecycle, and routes
 * external events (e.g. physics collisions delivered through `triggerEvent`).
 *
 * Variable state lives as own fields on the behaviour instance, so {@link getVariableSnapshot}
 * is the basis for hot-reload state preservation (snapshot → reload code → re-apply by name).
 */

import type { EntityHandle, IEngineContext } from "./IEngineContext";
import { NO_ENTITY } from "./IEngineContext";
import type { IScriptExecutor, ScriptDefinition } from "./IScriptExecutor";
import { Behaviour } from "./Behaviour";

/** A constructor for an authored Behaviour (what a compiled TS script module exports). */
export type BehaviourCtor = new () => Behaviour;

export class TypeScriptExecutor implements IScriptExecutor {
  private readonly _behaviour: Behaviour;
  private _entity: EntityHandle = NO_ENTITY;

  constructor(behaviour: Behaviour) {
    this._behaviour = behaviour;
  }

  /** Construct from a Behaviour class (the typical backend path). */
  static fromClass(ctor: BehaviourCtor): TypeScriptExecutor {
    return new TypeScriptExecutor(new ctor());
  }

  initialize(entity: EntityHandle, ctx: IEngineContext): void {
    this._entity = entity;
    this._behaviour.__bind(ctx);
  }

  onAwake(): void {
    this._behaviour.onAwake();
  }
  onEnable(): void {
    this._behaviour.onEnable();
  }
  onStart(): void {
    this._behaviour.onStart();
  }
  onUpdate(dt: number): void {
    this._behaviour.onUpdate(dt);
  }
  onFixedUpdate(dt: number): void {
    this._behaviour.onFixedUpdate(dt);
  }
  onLateUpdate(dt: number): void {
    this._behaviour.onLateUpdate(dt);
  }
  onDisable(): void {
    this._behaviour.onDisable();
  }
  onDestroy(): void {
    this._behaviour.onDestroy();
  }

  getVariable<T = unknown>(name: string): T {
    return (this._behaviour as unknown as Record<string, unknown>)[name] as T;
  }

  setVariable(name: string, value: unknown): void {
    (this._behaviour as unknown as Record<string, unknown>)[name] = value;
  }

  /** Snapshot author-declared fields (own, non-internal) for hot-reload preservation. */
  getVariableSnapshot(): Record<string, unknown> {
    const out: Record<string, unknown> = {};
    const obj = this._behaviour as unknown as Record<string, unknown>;
    for (const key of Object.keys(obj)) {
      if (key === "ctx" || key.startsWith("_")) continue;
      out[key] = obj[key];
    }
    return out;
  }

  /**
   * Deliver an external event by invoking the matching Behaviour method. Two id forms are
   * accepted so one canonical dispatch reaches both backends symmetrically:
   *  - the canonical engine form `events.collisionEnter` → `behaviour.onCollisionEnter` (the
   *    same id the Redprint backend consumes; the host physics subsystem emits this), and
   *  - a direct method name `onCollisionEnter` → `behaviour.onCollisionEnter`.
   */
  triggerEvent(eventId: string, params?: Record<string, unknown>): void {
    const method = eventId.startsWith("events.")
      ? "on" + eventId.charAt(7).toUpperCase() + eventId.slice(8)
      : eventId;
    const handler = (this._behaviour as unknown as Record<string, unknown>)[method];
    if (typeof handler === "function") {
      (handler as (p?: Record<string, unknown>) => void).call(this._behaviour, params);
    }
  }

  getDefinition(): ScriptDefinition {
    return {
      name: this._behaviour.constructor.name,
      fields: Object.keys(this.getVariableSnapshot()).map((name) => ({ name, type: "unknown" })),
      methods: [],
      executeOrder: 0,
    };
  }

  /** The bound entity (mainly for diagnostics). */
  get entity(): EntityHandle {
    return this._entity;
  }
}
